import { useMemo } from "react";
import { Files } from "lucide-react";

import { Conversation } from "shared/apiClient";
import { getText, interpolate } from "shared/lib";

import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  useToast,
} from "shared/components";
import { useCaptureEvent, CaptureEventType } from "@/hooks";

interface ShareDialogProps {
  conversation: Conversation | undefined | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareDialog({
  conversation,
  onOpenChange,
  open,
}: ShareDialogProps) {
  const { toast } = useToast();
  const { captureEvent } = useCaptureEvent();

  const url = useMemo(() => {
    return `${window.location.protocol}//${window.location.host}/report?id=${conversation?.identifier?.toUpperCase()}`;
  }, [conversation?.identifier]);

  const handleClickCopy = () => {
    try {
      navigator.clipboard.writeText(url);
      toast({
        title: getText("Share link copied to clipboard."),
      });
    } catch (error) {
      console.error("<SharedDialog> handleClickCopy() error", error);
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent aria-describedby={undefined}>
        <DialogTitle>{getText("Share")}</DialogTitle>

        <div className="flex flex-col justify-normal gap-2 overflow-hidden">
          <p className="text-bodylg text-textPrimary">
            {getText(
              "Share your analysis with others using the following link:",
            )}
          </p>

          <div className="border-interactiveBorder flex items-center gap-4 rounded-sm border px-2 py-1.5">
            <a
              className="text-bodymd text-textLink hover:text-textLinkHover flex-1 cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap underline"
              href={url}
              rel="noreferrer"
              tabIndex={0}
              target="_blank"
              onClick={() => {
                captureEvent(CaptureEventType.USER_CLICKED_REPORT_LINK);
              }}
            >
              {url}
            </a>

            <Button
              className="text-bodymd text-textLink bg-blueBgDefault m-0 h-auto rounded-sm !px-1.5 !py-0.5"
              onClick={() => {
                captureEvent(CaptureEventType.USER_CLICKED_COPY_SHARE_LINK);
                handleClickCopy();
              }}
              variant="link"
            >
              <Files /> {getText("Copy share link")}
            </Button>
          </div>
        </div>

        <p className="text-bodylg text-textPrimary">
          {interpolate(
            "This link is also emailed to {{userEmail}} after the permit and application requirements have been reviewed by myLot's AI.",
            {
              userEmail: <b>{conversation?.user_email}</b>,
            },
          )}
        </p>
      </DialogContent>
    </Dialog>
  );
}
